<template>
  <div
    ref="gallery"
    :class="$style.gallery"
    @mouseenter="pauseOnHover"
    @mouseleave="resumeAfterHover"
  >
    <div
      :class="$style.stage"
      @touchstart.passive="handleTouchStart"
      @touchend.passive="handleTouchEnd"
      @touchcancel="resetTouch"
    >
      <button
        type="button"
        :class="$style.mainImage"
        aria-label="Show next image"
        @click="handleMainImageClick"
      >
        <Transition
          :enter-active-class="$style.imageEnterActive"
          :leave-active-class="$style.imageLeaveActive"
          :enter-from-class="$style.imageEnterFrom"
          :leave-to-class="$style.imageLeaveTo"
        >
          <img
            v-if="activeImage"
            :key="activeImage"
            :src="activeImage"
            :alt="`${altPrefix} ${activeIndex + 1}`"
            :class="$style.image"
            decoding="async"
            draggable="false"
          />
        </Transition>
      </button>

      <button
        type="button"
        :class="[$style.arrow, $style.previous]"
        aria-label="Show previous image"
        @click="previous"
      >
        <span aria-hidden="true"></span>
      </button>
      <button
        type="button"
        :class="[$style.arrow, $style.next]"
        aria-label="Show next image"
        @click="next"
      >
        <span aria-hidden="true"></span>
      </button>
    </div>

    <div :class="$style.thumbnails">
      <button
        v-for="image in thumbnailImages"
        :key="image.src"
        type="button"
        :class="$style.thumbnail"
        :aria-label="`Show image ${image.index + 1}`"
        @click="selectImage(image.index)"
      >
        <img :src="image.src" alt="" decoding="async" draggable="false" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    images: {
      type: Array,
      required: true,
    },
    altPrefix: {
      type: String,
      default: "School garden",
    },
    autoplayDelay: {
      type: Number,
      default: 3000,
    },
  },
  data() {
    return {
      activeIndex: 0,
      autoplayTimer: null,
      intersectionObserver: null,
      motionPreference: null,
      isVisible: true,
      isDocumentVisible: !document.hidden,
      prefersReducedMotion: false,
      isHovered: false,
      touchStartX: null,
      touchStartY: null,
      ignoreClickUntil: 0,
    };
  },
  computed: {
    activeImage() {
      return this.images[this.activeIndex];
    },
    thumbnailImages() {
      return this.images
        .map((src, index) => ({ src, index }))
        .filter((image) => image.index !== this.activeIndex);
    },
    canAutoplay() {
      return (
        this.images.length > 1 &&
        this.isVisible &&
        this.isDocumentVisible &&
        !this.prefersReducedMotion &&
        !this.isHovered
      );
    },
  },
  watch: {
    images() {
      this.activeIndex = 0;
      this.preloadImages();
      this.scheduleAutoplay();
    },
  },
  mounted() {
    this.preloadImages();
    this.setupMotionPreference();
    this.setupIntersectionObserver();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.scheduleAutoplay();
  },
  beforeUnmount() {
    this.clearAutoplay();
    this.intersectionObserver?.disconnect();
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );

    if (this.motionPreference) {
      if (this.motionPreference.removeEventListener) {
        this.motionPreference.removeEventListener(
          "change",
          this.handleMotionPreference
        );
      } else {
        this.motionPreference.removeListener(this.handleMotionPreference);
      }
    }
  },
  methods: {
    changeImage(step) {
      const imageCount = this.images.length;

      if (imageCount < 2) {
        return;
      }

      this.activeIndex = (this.activeIndex + step + imageCount) % imageCount;
    },
    next() {
      this.changeImage(1);
      this.scheduleAutoplay();
    },
    previous() {
      this.changeImage(-1);
      this.scheduleAutoplay();
    },
    selectImage(index) {
      this.activeIndex = index;
      this.scheduleAutoplay();
    },
    handleMainImageClick() {
      if (Date.now() < this.ignoreClickUntil) {
        return;
      }

      this.next();
    },
    handleTouchStart(event) {
      const touch = event.touches[0];

      if (!touch) {
        return;
      }

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
    },
    handleTouchEnd(event) {
      const touch = event.changedTouches[0];

      if (!touch || this.touchStartX === null || this.touchStartY === null) {
        this.resetTouch();
        return;
      }

      const distanceX = touch.clientX - this.touchStartX;
      const distanceY = touch.clientY - this.touchStartY;
      const isHorizontalSwipe =
        Math.abs(distanceX) >= 50 && Math.abs(distanceX) > Math.abs(distanceY);

      this.resetTouch();

      if (!isHorizontalSwipe) {
        return;
      }

      this.ignoreClickUntil = Date.now() + 400;

      if (distanceX < 0) {
        this.next();
      } else {
        this.previous();
      }
    },
    resetTouch() {
      this.touchStartX = null;
      this.touchStartY = null;
    },
    clearAutoplay() {
      if (this.autoplayTimer !== null) {
        window.clearTimeout(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    },
    scheduleAutoplay() {
      this.clearAutoplay();

      if (!this.canAutoplay) {
        return;
      }

      this.autoplayTimer = window.setTimeout(() => {
        this.changeImage(1);
        this.scheduleAutoplay();
      }, this.autoplayDelay);
    },
    handleVisibilityChange() {
      this.isDocumentVisible = !document.hidden;
      this.scheduleAutoplay();
    },
    pauseOnHover() {
      if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        this.isHovered = true;
        this.clearAutoplay();
      }
    },
    resumeAfterHover() {
      this.isHovered = false;
      this.scheduleAutoplay();
    },
    setupIntersectionObserver() {
      if (!("IntersectionObserver" in window)) {
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.scheduleAutoplay();
        },
        { threshold: 0.1 }
      );
      this.intersectionObserver.observe(this.$refs.gallery);
    },
    setupMotionPreference() {
      this.motionPreference = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.prefersReducedMotion = this.motionPreference.matches;

      if (this.motionPreference.addEventListener) {
        this.motionPreference.addEventListener(
          "change",
          this.handleMotionPreference
        );
      } else {
        this.motionPreference.addListener(this.handleMotionPreference);
      }
    },
    handleMotionPreference(event) {
      this.prefersReducedMotion = event.matches;
      this.scheduleAutoplay();
    },
    preloadImages() {
      this.images.forEach((src) => {
        const image = new Image();
        image.src = src;
      });
    },
  },
};
</script>

<style lang="scss" module>
.gallery {
  width: 100%;
}

.stage {
  position: relative;
  touch-action: pan-y;
}

.mainImage {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 1.25rem;
  background-color: $light-brown;
  cursor: pointer;
}

.image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.imageEnterActive,
.imageLeaveActive {
  transition: opacity 0.35s ease;
}

.imageLeaveActive {
  position: absolute;
}

.imageEnterFrom,
.imageLeaveTo {
  opacity: 0;
}

.arrow {
  position: absolute;
  top: 50%;
  z-index: 2;
  width: 3rem;
  height: 4rem;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  transform: translateY(-50%);

  span {
    display: block;
    width: 1.75rem;
    height: 1.75rem;
    border-bottom: 0.25rem solid $orange;
    border-left: 0.25rem solid $orange;
  }
}

.previous {
  left: 1rem;

  span {
    transform: rotate(45deg);
  }
}

.next {
  right: 1rem;

  span {
    margin-left: auto;
    transform: rotate(225deg);
  }
}

.thumbnails {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.thumbnail {
  aspect-ratio: 4 / 3;
  padding: 0;
  overflow: hidden;
  border: 0;
  border-radius: 0.75rem;
  background-color: $light-brown;
  cursor: pointer;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.25s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }
}

@media (any-pointer: coarse) {
  .arrow {
    display: none;
  }

  .thumbnail:hover img {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .imageEnterActive,
  .imageLeaveActive,
  .thumbnail img {
    transition: none;
  }
}
</style>
